import { useState, useRef, useEffect } from "react";

import { ChevronDown, ChevronUp, Copy, Check } from "lucide-react";

import { toast } from "@/components/ui/sonner";
import { cn } from "@/lib/utils";

import { Button } from "./button";
import { Markdown } from "./markdown";

interface CollapsibleMarkdownProps {
  content: string;
  className?: string;
  collapsedHeight?: number; // Height in pixels before collapsing
  collapseEnabled?: boolean; // Whether to enable collapsing
}

export const CollapsibleMarkdown = ({ 
  content, 
  className,
  collapsedHeight = 400,
  collapseEnabled = true
}: CollapsibleMarkdownProps) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [needsCollapse, setNeedsCollapse] = useState(false);
  const [copied, setCopied] = useState(false);
  const [currentHeight, setCurrentHeight] = useState(collapsedHeight);
  const contentRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Check if content exceeds the collapsed height
    if (contentRef.current) {
      const contentHeight = contentRef.current.scrollHeight;
      setNeedsCollapse(contentHeight > collapsedHeight && collapseEnabled);
    }
  }, [content, collapsedHeight, collapseEnabled]);

  // Scroll-based progressive expansion
  useEffect(() => {
    if (!needsCollapse || isExpanded || !containerRef.current || !contentRef.current) return;

    const handleScroll = () => {
      if (!containerRef.current || !contentRef.current) return;
      
      const container = containerRef.current;
      const scrollTop = container.scrollTop;
      const scrollHeight = container.scrollHeight;
      const clientHeight = container.clientHeight;
      
      // Calculate how much to expand based on scroll position
      const scrollableDistance = scrollHeight - clientHeight;
      const scrollPercentage = scrollableDistance > 0 ? scrollTop / scrollableDistance : 0;
      
      // If user scrolls to bottom 20%, expand by 50%
      if (scrollPercentage > 0.8) {
        const fullHeight = contentRef.current.scrollHeight;
        const newHeight = Math.min(fullHeight, currentHeight + 200);
        
        if (newHeight < fullHeight) {
          setCurrentHeight(newHeight);
        } else {
          // Reached full height, mark as expanded
          setIsExpanded(true);
        }
      }
    };

    const container = containerRef.current;
    container.addEventListener('scroll', handleScroll);
    
    return () => container.removeEventListener('scroll', handleScroll);
  }, [needsCollapse, isExpanded, currentHeight]);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(content);
      setCopied(true);
      toast.success("Copied to clipboard");
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error("Failed to copy:", error);
      toast.error("Failed to copy");
    }
  };

  if (!needsCollapse) {
    // Content is short enough or collapsing is disabled, render normally with copy button
    return (
      <div className="relative group">
        <Markdown content={content} className={className} />
        <Button
          variant="ghost"
          size="sm"
          onClick={handleCopy}
          className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity gap-2"
          title="Copy response"
        >
          {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
        </Button>
      </div>
    );
  }

  return (
    <div className="relative group">
      <div
        ref={containerRef}
        className={cn(
          "transition-all duration-300 ease-in-out overflow-auto",
          className
        )}
        style={{
          maxHeight: isExpanded ? `${contentRef.current?.scrollHeight}px` : `${currentHeight}px`,
        }}
      >
        <div ref={contentRef}>
          <Markdown content={content} />
        </div>
      </div>
      
      {/* Gradient fade effect when collapsed */}
      {!isExpanded && (
        <div 
          className="absolute bottom-0 left-0 right-0 h-24 bg-linear-to-t from-muted to-transparent pointer-events-none"
        />
      )}
      
      {/* Copy button */}
      <Button
        variant="ghost"
        size="sm"
        onClick={handleCopy}
        className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity gap-2"
        title="Copy full response"
      >
        {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
      </Button>
      
      {/* Expand/Collapse button */}
      <div className="flex justify-center mt-2">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => {
            setIsExpanded(!isExpanded);
            setCurrentHeight(collapsedHeight);
          }}
          className="gap-2"
        >
          {isExpanded ? (
            <>
              Show less
              <ChevronUp className="h-4 w-4" />
            </>
          ) : (
            <>
              Read more {!isExpanded && currentHeight > collapsedHeight && "(scroll to expand)"}
              <ChevronDown className="h-4 w-4" />
            </>
          )}
        </Button>
      </div>
    </div>
  );
};
